/*
  Carrousels du site (familles, rayon érable, plus demandés) — infinis et fluides.

  Pourquoi pas le défilement natif : avancer scrollLeft à chaque image passe par le fil
  principal du navigateur et s'arrondit au pixel. Sur iPhone ça donnait des à-coups, et
  toucher la rangée l'arrêtait net. Ici la rangée est déplacée par transform (translate3d),
  que la carte graphique compose seule : c'est ce que fait GSAP, sans rien de plus à charger.

  - La liste est recopiée trois fois ; la position est ramenée dans la copie du milieu par
    un modulo : on ne voit jamais de bout, dans un sens comme dans l'autre.
  - L'avance dépend du temps écoulé : même vitesse sur tous les écrans.
  - Doigt ou souris : on attrape et on tire ; au lâcher, l'élan du geste s'amortit puis la
    rangée reprend sa vitesse de croisière, sans pause.
  - Survol à la souris : la rangée ralentit (pour pouvoir cliquer), elle ne s'arrête pas.
  - Flèches : une carte à la fois, avec une courbe douce.
  - Trackpad à deux doigts (ou Maj + molette) : la rangée suit le geste.
  - Hors de l'écran, l'animation dort : aucun coût pour le reste de la page.

  Réglages par attribut sur la zone :
    data-cbl-defile            active le carrousel
    data-cbl-vitesse="22"      pixels par seconde (0 = pas d'avance automatique)
*/
(() => {
  const calme = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initialiser = (zone) => {
    if (zone.dataset.cblPret) return;
    zone.dataset.cblPret = '1';
    const piste = zone.querySelector('[data-cbl-piste]') || zone.querySelector('ul');
    if (!piste || piste.children.length < 2) return;

    const croisiere = calme ? 0 : parseFloat(zone.dataset.cblVitesse ?? '22');
    zone.style.overflow = 'hidden';
    zone.style.touchAction = 'pan-y';
    piste.style.willChange = 'transform';

    const originaux = [...piste.children];
    for (const cote of ['avant', 'apres']) {
      const lot = document.createDocumentFragment();
      originaux.forEach((el) => {
        const copie = el.cloneNode(true);
        copie.setAttribute('aria-hidden', 'true');
        // visibles et cliquables, mais hors du clavier et des lecteurs d'écran
        copie.querySelectorAll('a, button, input, select').forEach((n) => n.setAttribute('tabindex', '-1'));
        copie.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
        lot.append(copie);
      });
      cote === 'avant' ? piste.prepend(lot) : piste.append(lot);
    }

    let largeurLot = 0;
    let pas = 0;
    const mesurer = () => {
      largeurLot = piste.children[originaux.length].offsetLeft - piste.children[0].offsetLeft;
      pas = piste.children[1].offsetLeft - piste.children[0].offsetLeft;
    };

    let x = 0;          // position, en pixels, dans la copie du milieu
    let vitesse = croisiere;
    let cible = croisiere;
    const peindre = () => {
      if (!largeurLot) return;
      x = ((x - largeurLot) % largeurLot + largeurLot) % largeurLot + largeurLot;
      piste.style.transform = `translate3d(${-x}px,0,0)`;
    };

    // Glisser au doigt ou à la souris
    let geste = null;
    let bouge = false;
    zone.addEventListener('pointerdown', (e) => {
      if (e.button > 0) return;
      geste = { id: e.pointerId, x0: e.clientX, y0: e.clientY, depart: x, dernierX: e.clientX, t: performance.now(), v: 0, horizontal: null };
      bouge = false;
      tween = null;
    });
    zone.addEventListener('pointermove', (e) => {
      if (!geste || e.pointerId !== geste.id) return;
      const dx = e.clientX - geste.x0;
      const dy = e.clientY - geste.y0;
      if (geste.horizontal === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        geste.horizontal = Math.abs(dx) > Math.abs(dy);
        if (geste.horizontal) {
          zone.setPointerCapture(e.pointerId);
          zone.classList.add('est-tire');
        }
      }
      if (!geste.horizontal) return;
      bouge = true;
      const maintenant = performance.now();
      const dt = Math.max(maintenant - geste.t, 1);
      geste.v = 0.8 * ((geste.dernierX - e.clientX) / dt) * 1000 + 0.2 * geste.v;
      geste.dernierX = e.clientX;
      geste.t = maintenant;
      x = geste.depart - dx;
      peindre();
    });
    const lacher = () => {
      if (!geste) return;
      if (bouge) vitesse = Math.max(-2500, Math.min(2500, geste.v)); // l'élan du geste
      geste = null;
      zone.classList.remove('est-tire');
    };
    zone.addEventListener('pointerup', lacher);
    zone.addEventListener('pointercancel', lacher);
    // Un tirage ne doit pas ouvrir la carte sous le doigt.
    zone.addEventListener('click', (e) => { if (bouge) { e.preventDefault(); e.stopPropagation(); bouge = false; } }, true);
    zone.addEventListener('dragstart', (e) => e.preventDefault());

    // Trackpad (glissé à deux doigts) et Maj + molette : la rangée suit le geste.
    // Un défilement vertical ordinaire passe à la page, sans être retenu.
    zone.addEventListener('wheel', (e) => {
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (!dx) return;
      e.preventDefault();
      tween = null;
      x += e.deltaMode === 1 ? dx * 40 : dx;
      peindre();
    }, { passive: false });

    // Survol : on ralentit pour laisser cliquer, sans s'arrêter.
    zone.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') cible = croisiere * 0.25; });
    zone.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') cible = croisiere; });

    // Flèches
    let tween = null;
    const racine = zone.closest('section') || document;
    racine.querySelectorAll('[data-cbl-fleche]').forEach((bouton) => {
      bouton.addEventListener('click', () => {
        const sens = bouton.dataset.cblFleche === 'avant' ? -1 : 1;
        tween = { de: x, a: x + sens * pas, t0: performance.now(), duree: 650 };
      });
    });

    // Boucle d'animation, endormie hors de l'écran
    let visible = false;
    let avant = null;
    let boucle = null;
    const tick = (t) => {
      const dt = avant === null ? 0 : Math.min(t - avant, 50) / 1000;
      avant = t;
      if (tween) {
        const p = Math.min((t - tween.t0) / tween.duree, 1);
        const e = 1 - Math.pow(1 - p, 3);
        x = tween.de + (tween.a - tween.de) * e;
        if (p >= 1) tween = null;
      } else if (!geste) {
        // l'élan s'amortit en douceur vers la vitesse de croisière
        vitesse += (cible - vitesse) * Math.min(1, dt * 3);
        x += vitesse * dt;
      }
      peindre();
      boucle = visible ? requestAnimationFrame(tick) : null;
    };
    const reveiller = () => {
      if (boucle === null && visible) { avant = null; boucle = requestAnimationFrame(tick); }
    };
    new IntersectionObserver(([entree]) => {
      visible = entree.isIntersecting;
      reveiller();
    }, { rootMargin: '100px' }).observe(zone);

    const demarrer = () => { mesurer(); x = largeurLot; peindre(); };
    demarrer();
    addEventListener('load', demarrer, { once: true });
    new ResizeObserver(() => { const r = largeurLot ? (x - largeurLot) / largeurLot : 0; mesurer(); x = largeurLot + r * largeurLot; peindre(); }).observe(piste);
  };

  const tout = () => document.querySelectorAll('[data-cbl-defile]').forEach(initialiser);
  if (document.readyState === 'loading') addEventListener('DOMContentLoaded', tout);
  else tout();
  document.addEventListener('shopify:section:load', tout);
})();
